/**
 * AdminBanner + AdminToast — inline status messaging for the admin
 * portal, matching /brand Aspect 14 (Banner). Replaces the bespoke
 * ErrorBanner / SavedToast re-implemented across most admin screens.
 * Three tones: success / error / info. Theme-driven — no hex.
 */
import type { ReactNode } from "react";
import { Platform, Text, View, type ViewStyle } from "react-native";

import { RADII, STROKES } from "../../../lib/brand";
import type { PaneTheme } from "../../brand/paneThemes";

export type AdminBannerTone = "success" | "error" | "info";

function toneColor(theme: PaneTheme, tone: AdminBannerTone): string {
  if (tone === "success") return theme.success;
  if (tone === "error") return theme.destructive;
  return theme.accent;
}

export type AdminBannerProps = {
  theme: PaneTheme;
  tone: AdminBannerTone;
  /** Optional bold title above the message. */
  title?: string;
  message: ReactNode;
  /** Optional trailing slot (e.g. a retry button). */
  trailing?: ReactNode;
  style?: ViewStyle;
};

export function AdminBanner({
  theme,
  tone,
  title,
  message,
  trailing,
  style,
}: AdminBannerProps) {
  const color = toneColor(theme, tone);
  return (
    <View
      style={[
        {
          flexDirection: "row",
          alignItems: "center",
          gap: 12,
          backgroundColor: theme.surfaceBg,
          borderWidth: STROKES.hairline,
          borderColor: theme.border,
          borderLeftWidth: STROKES.heavy,
          borderLeftColor: color,
          borderRadius: RADII.md,
          paddingVertical: 12,
          paddingHorizontal: 14,
        },
        style ?? null,
      ]}
    >
      <View
        style={{
          width: 8,
          height: 8,
          borderRadius: RADII.full,
          backgroundColor: color,
        }}
      />
      <View style={{ flex: 1, gap: 3 }}>
        {title ? (
          <Text
            style={{
              fontFamily: theme.bodySemiBoldFont,
              fontSize: 12.5,
              color: theme.textOnSurface,
            }}
          >
            {title}
          </Text>
        ) : null}
        {typeof message === "string" ? (
          <Text
            style={{
              fontFamily: theme.bodyFont,
              fontSize: 12,
              color: title ? theme.textOnSurfaceDim : color,
            }}
          >
            {message}
          </Text>
        ) : (
          message
        )}
      </View>
      {trailing != null ? trailing : null}
    </View>
  );
}

/**
 * AdminToast — a floating, self-contained confirmation pill. Render it
 * conditionally (e.g. after a save) near the action it confirms.
 */
export function AdminToast({
  theme,
  tone = "success",
  message,
}: {
  theme: PaneTheme;
  tone?: AdminBannerTone;
  message: string;
}) {
  const color = toneColor(theme, tone);
  return (
    <View
      style={[
        {
          flexDirection: "row",
          alignItems: "center",
          gap: 8,
          alignSelf: "flex-start",
          backgroundColor: theme.surfaceRaisedBg,
          borderWidth: STROKES.hairline,
          borderColor: color,
          borderRadius: RADII.full,
          paddingVertical: 7,
          paddingHorizontal: 14,
        },
        Platform.OS === "web"
          ? ({ boxShadow: "0 4px 14px rgba(0,0,0,0.25)" } as unknown as ViewStyle)
          : null,
      ]}
    >
      <View
        style={{
          width: 7,
          height: 7,
          borderRadius: RADII.full,
          backgroundColor: color,
        }}
      />
      <Text
        style={{
          fontFamily: theme.bodySemiBoldFont,
          fontSize: 12,
          color: theme.textOnSurface,
        }}
      >
        {message}
      </Text>
    </View>
  );
}
